import {
  Box,
  Button,
  FormControl,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import axios from "axios";
import { useRef, useState } from "react";
import { ChatState } from "../../Context/ChatProvider";
import UserBadgeItem from "../userAvatar/UserBadgeItem";
import UserListItem from "../userAvatar/UserListItem";

const GroupChatModal = ({ children }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [groupChatName, setGroupChatName] = useState("");
  const [groupDescription, setGroupDescription] = useState("");
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [searchResult, setSearchResult] = useState([]);
  const [loading, setLoading] = useState(false);
  const toast = useToast();
  const searchTimeout = useRef(null);

  const { user, chats, setChats } = ChatState();

  const resetState = () => {
    setGroupChatName("");
    setGroupDescription("");
    setSelectedUsers([]);
    setSearch("");
    setSearchResult([]);
    setLoading(false);
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
      searchTimeout.current = null;
    }
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleGroup = (userToAdd) => {
    if (selectedUsers.includes(userToAdd)) {
      toast({
        title: "User already added",
        status: "warning",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
      return;
    }

    setSelectedUsers([...selectedUsers, userToAdd]);
  };

  const handleSearch = async (query) => {
    setSearch(query);
    if (!query) {
      setSearchResult([]);
      setLoading(false);
      return;
    }

    if (!user?.token) return;

    // Debounce: wait 300ms after last keystroke before hitting the API.
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
    searchTimeout.current = setTimeout(async () => {
      try {
        setLoading(true);
        const config = {
          headers: {
            Authorization: `Bearer ${user?.token}`,
          },
        };
        const { data } = await axios.get(
          `/api/user?search=${encodeURIComponent(query)}`,
          config
        );
        console.log(data);
        setSearchResult(data);
      } catch (error) {
        toast({
          title: "Error Occured!",
          description: "Failed to Load the Search Results",
          status: "error",
          duration: 5000,
          isClosable: true,
          position: "bottom-left",
        });
      } finally {
        setLoading(false);
      }
    }, 300);
  };

  const handleDelete = (delUser) => {
    setSelectedUsers(selectedUsers.filter((sel) => sel._id !== delUser._id));
  };

  const handleSubmit = async () => {
    if (!groupChatName || selectedUsers.length === 0) {
      toast({
        title: "Please fill all the Fields",
        status: "warning",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
      return;
    }

    try {
      const config = {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
      };
      const { data } = await axios.post(
        `/api/chat/group`,
        {
          name: groupChatName,
          users: JSON.stringify(selectedUsers.map((u) => u._id)),
          description: groupDescription.trim(),
        },
        config
      );
      setChats([data, ...(chats || [])]);
      handleClose();
      toast({
        title: "New Group Chat Created!",
        status: "success",
        duration: 5000,
        isClosable: true,
        position: "bottom",
      });
    } catch (error) {
      toast({
        title: "Failed to Create the Chat!",
        description:
          error.response?.data?.message || error.response?.data || error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "bottom",
      });
    }
  };

  return (
    <>
      <span onClick={onOpen}>{children}</span>

      <Modal onClose={handleClose} isOpen={isOpen} isCentered>
        <ModalOverlay bg="rgba(2,6,23,0.7)" backdropFilter="blur(6px)" />
        <ModalContent
          bg="var(--ch-modal)"
          color="var(--ch-text)"
          border="1px solid var(--ch-panel-border)"
          borderRadius="20px"
        >
          <ModalHeader
            fontSize="35px"
            fontFamily="Sora"
            fontWeight="800"
            display="flex"
            justifyContent="center"
          >
            Create Group Chat
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody display="flex" flexDir="column" alignItems="center">
            <FormControl>
              <Input
                placeholder="Chat Name"
                mb={3}
                className="chat-input"
                value={groupChatName}
                onChange={(e) => setGroupChatName(e.target.value)}
              />
            </FormControl>
            <FormControl>
              <Input
                placeholder="Group description (optional, 500 chars max)"
                mb={3}
                className="chat-input"
                value={groupDescription}
                onChange={(e) => setGroupDescription(e.target.value)}
              />
            </FormControl>
            <FormControl>
              <Input
                placeholder="Add Users"
                mb={1}
                className="chat-input"
                value={search}
                onChange={(e) => handleSearch(e.target.value)}
              />
            </FormControl>
            <Box w="100%" display="flex" flexWrap="wrap">
              {selectedUsers.map((u) => (
                <UserBadgeItem
                  key={u._id}
                  user={u}
                  handleFunction={() => handleDelete(u)}
                />
              ))}
            </Box>
            {loading ? (
              // <ChatLoading />
              <div style={{ color: "var(--ch-faint)" }}>Loading...</div>
            ) : (
              <>
                {search.trim() && (
                  <Text
                    fontSize="xs"
                    color="var(--ch-faint)"
                    alignSelf="flex-start"
                    w="100%"
                    mb={1}
                  >
                    {searchResult.length === 0
                      ? "No users found"
                      : `${searchResult.length} user${searchResult.length === 1 ? "" : "s"} found`}
                  </Text>
                )}
                <Box
                  w="100%"
                  maxH="260px"
                  overflowY="auto"
                  className="dark-scroll"
                  display="flex"
                  flexDir="column"
                  gap={1}
                >
                  {searchResult?.map((user) => (
                    <UserListItem
                      key={user._id}
                      user={user}
                      handleFunction={() => handleGroup(user)}
                    />
                  ))}
                </Box>
                {search.trim() && !loading && searchResult.length === 0 && (
                  <Text fontSize="xs" color="var(--ch-faint)" mt={1}>
                    Try a different name or email.
                  </Text>
                )}
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Button className="chat-btn-primary" onClick={handleSubmit}>
              Create Chat
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default GroupChatModal;
